import React, { memo } from 'react';
import ErrorQueryResults from '../../QueryResult/ErrorQueryResults';
import ErrorBoundary from '../../error/ErrorBoundary';
import QueryResult from '../QueryResult';
import useSortablePaginationQueryResultLogic, {
  HookProps,
} from './useSortablePaginationQueryResultLogic';

interface Props extends HookProps {
  savable?: boolean;
  className?: string;
  classes?: Record<'graphView', string>;
  showResult?: boolean;
  parentSelectedRowID?: string | null;
  onRemoveResult?: VoidFunction;
  parentSetSelectedRowID?: (id: string | null) => void;
}

const SortablePaginationQueryResult = ({
  onLoaded,
  query,
  standardId,
  showResult = true,
  onRemoveResult,
  ...props
}: Props) => {
  const {
    queryError,
    loading,
    result,
    refetch,
    loadMoreOrderBy,
    handleResultsChange,
    loadMore,
    totalResultCount,
  } = useSortablePaginationQueryResultLogic({
    query,
    standardId,
    onLoaded,
  });

  if (!showResult) {
    return null;
  }

  if (queryError)
    return (
      <ErrorQueryResults
        query={query.query}
        message={queryError.message}
        onRemoveResult={onRemoveResult}
      />
    );

  return (
    <ErrorBoundary>
      <QueryResult
        queryInput={query}
        result={result || undefined}
        onResultsChange={handleResultsChange}
        loading={loading}
        {...props}
        loadMore={loadMore}
        refetch={refetch}
        loadMoreOrderBy={loadMoreOrderBy}
        onRemoveResult={onRemoveResult}
        totalResultCount={totalResultCount}
      />
    </ErrorBoundary>
  );
};

// WARNING: manual prop testing is dangerous
// Currently memo will assume all functions are statically set
// This is needed since query is an object and a shallow diff will return false (should rerender)
export default memo(SortablePaginationQueryResult, (prevProps, nextProps) => {
  if (prevProps.query?.query !== nextProps.query?.query) return false;
  if (prevProps.standardId !== nextProps.standardId) return false;
  if (prevProps.savable !== nextProps.savable) return false;
  if (prevProps.showResult !== nextProps.showResult) return false;
  if (prevProps.onRemoveResult !== nextProps.onRemoveResult) return false;
  return true;
});
